import React from "react";
import { Badge, Row, Col } from "react-bootstrap";
import DetailField from "./DetailField";

const InfoTab = ({ customer, onEdit }) => {
  const isOrganization = customer.type === "Organization";

  const InfoBlock = ({ title, children }) => (
    <div className="mb-5">
      <h6 className="fw-bold mb-3 fs-13 text-black text-uppercase letter-spacing-1">
        {title}
      </h6>
      <div
        className="border p-4 bg-white shadow-sm"
        style={{ borderRadius: 12, borderColor: "#EAECF0" }}
      >
        {children}
      </div>
    </div>
  );

  const renderWalletBalance = () => (
    <InfoBlock title="Wallet Balance">
      <div className="table-responsive">
        <table className="table fs-13 mb-0">
          <thead className="bg-light">
            <tr>
              <th className="fw-bold border-0">Currency</th>
              <th className="fw-bold border-0 text-center">
                Available balance
              </th>
              <th className="fw-bold border-0 text-center">Pending balance</th>
            </tr>
          </thead>
          <tbody>
            {customer.wallets?.map((w, i) => (
              <tr key={i}>
                <td className="border-bottom-0">{w.currency}</td>
                <td className="border-bottom-0 text-center">{w.available}</td>
                <td className="border-bottom-0 text-center">{w.pending}</td>
              </tr>
            )) || (
              <tr>
                <td colSpan="3" className="text-center py-4 text-muted">
                  No wallets found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </InfoBlock>
  );

  const renderUsageLimits = () => (
    <InfoBlock title="Usage Limits">
      <div className="table-responsive">
        <table className="table fs-13 mb-0 align-middle">
          <thead className="bg-light">
            <tr>
              <th className="fw-bold border-0">Receiver Type</th>
              <th className="fw-bold border-0 text-center">Daily</th>
              <th className="fw-bold border-0 text-center">Monthly</th>
              <th className="fw-bold border-0 text-center">Yearly</th>
              <th className="fw-bold border-0 text-center">Past 30 Days</th>
              <th className="fw-bold border-0 text-center">Past 12 Months</th>
            </tr>
          </thead>
          <tbody>
            {customer.usageLimits?.map((l, i) => (
              <tr key={i}>
                <td className="border-bottom-0 fw-medium">{l.type}</td>
                <td className="border-bottom-0 text-center">{l.daily}</td>
                <td className="border-bottom-0 text-center">{l.monthly}</td>
                <td className="border-bottom-0 text-center">{l.yearly}</td>
                <td
                  className="border-bottom-0 text-center"
                  style={{ color: l.color === "red" ? "#F23244" : "inherit" }}
                >
                  {l.past30}
                </td>
                <td className="border-bottom-0 text-center">{l.past12}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </InfoBlock>
  );

  const renderDocuments = () => (
    <div className="mb-5">
      <h6 className="fw-bold mb-3 fs-13 text-black text-uppercase letter-spacing-1">
        Documents
      </h6>
      <div className="d-flex gap-4 flex-wrap">
        {customer.documents && customer.documents.length > 0 ? (
          customer.documents.map((doc, i) => (
            <div key={i} className="text-center" style={{ width: "160px" }}>
              <div className="text-muted fs-11 mb-2 text-start">
                {doc.label}
              </div>
              <div
                className="position-relative border rounded p-2 bg-white"
                style={{ height: "135px", borderColor: "#EAECF0" }}
              >
                <img
                  src={doc.image}
                  alt={doc.label}
                  className="w-100 h-100 object-fit-cover rounded"
                />
                <button
                  className="btn btn-white btn-xs position-absolute bottom-0 end-0 m-1 shadow-sm border"
                  style={{ padding: "4px 6px" }}
                >
                  <i className="fa fa-download fs-10 text-muted"></i>
                </button>
              </div>
            </div>
          ))
        ) : (
          <div
            className="border p-4 bg-white shadow-sm w-100 text-center text-muted fs-12"
            style={{ borderRadius: 12, borderColor: "#EAECF0" }}
          >
            No documents uploaded
          </div>
        )}
      </div>
    </div>
  );

  const renderIndividualInfo = () => (
    <>
      <InfoBlock title="Personal Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Customer Type" value={customer.type} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="First Name" value={customer.firstName || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Middle Name"
              value={customer.middleName || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Last Name" value={customer.lastName || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Date of Birth" value={customer.dob || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Nationality"
              value={customer.nationality || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Country of Birth"
              value={customer.countryOfBirth || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Gender" value={customer.gender || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Occupation"
              value={customer.occupation || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="Contact Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Phone Number" value={customer.phone || "-"} />
          </Col>
          <Col xl={4} lg={8} md={6}>
            <DetailField label="Email" value={customer.email || "-"} />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="Address Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Country" value={customer.country || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="City" value={customer.city || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Address Line 1"
              value={customer.addressLine1 || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Address Line 2"
              value={customer.addressLine2 || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Postal Code"
              value={customer.postalCode || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="ID Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="ID Type" value={customer.idType || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="ID Number" value={customer.idNumber || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Issue Date"
              value={customer.idIssueDate || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Expiry Date"
              value={customer.idExpiryDate || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="EKYC Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="AML Score" value={customer.amlScore || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="AML Risk Rating"
              value={customer.amlRiskRating || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="EKYC Status"
              value={customer.ekycStatus || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      {renderWalletBalance()}
      {renderUsageLimits()}
      {renderDocuments()}
    </>
  );

  const renderOrganizationInfo = () => (
    <>
      <InfoBlock title="Personal Information (Main Contact)">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Customer Type" value={customer.type} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Full Name" value={customer.fullName || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Date of Birth" value={customer.dob || "-"} />
          </Col>
          <Col xl={4} lg={8} md={6}>
            <DetailField label="Email" value={customer.email || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Nationality"
              value={customer.nationality || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Job Title" value={customer.jobTitle || "-"} />
          </Col>
          <Col xl={4} lg={8} md={6}>
            <DetailField
              label="Residential Address"
              value={customer.residentialAddress || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="Organization Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Registered Name"
              value={customer.registeredName || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="UEN/Registration Number"
              value={customer.uen || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Type of Entity"
              value={customer.entityType || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Date of Incorporation"
              value={customer.incorporationDate || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Business Activity"
              value={customer.businessActivity || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Office Phone Number"
              value={customer.officePhone || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Primary Email Address"
              value={customer.primaryEmail || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Number of Local Branches"
              value={customer.localBranches || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Number of Overseas Branches"
              value={customer.overseasBranches || "-"}
            />
          </Col>
          <Col xl={4} lg={8} md={6}>
            <DetailField
              label="Countries Overseas Branches are Situated in"
              value={customer.overseasCountries || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="Organization Address">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="Country" value={customer.country || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="City" value={customer.city || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Address Line 1"
              value={customer.addressLine1 || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Address Line 2"
              value={customer.addressLine2 || "-"}
            />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Postal Code"
              value={customer.postalCode || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      <InfoBlock title="ID Information">
        <Row className="gy-4">
          <Col xl={2} lg={4} md={6}>
            <DetailField label="ID Type" value={customer.idType || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField label="ID Number" value={customer.idNumber || "-"} />
          </Col>
          <Col xl={2} lg={4} md={6}>
            <DetailField
              label="Expiry Date"
              value={customer.idExpiryDate || "-"}
            />
          </Col>
        </Row>
      </InfoBlock>

      {renderWalletBalance()}
      {renderUsageLimits()}
      {renderDocuments()}

      <div className="mb-5">
        <h6 className="fw-bold mb-3 fs-13 text-black text-uppercase letter-spacing-1">
          Beneficial Owners
        </h6>
        <div
          className="border bg-white shadow-sm"
          style={{ borderRadius: 12, borderColor: "#EAECF0" }}
        >
          {customer.beneficialOwners && customer.beneficialOwners.length > 0 ? (
            customer.beneficialOwners.map((owner, idx) => (
              <div
                key={idx}
                className={`p-4 ${idx < customer.beneficialOwners.length - 1 ? "border-bottom" : ""}`}
                style={{ borderColor: "#EAECF0" }}
              >
                <Row className="gy-4">
                  <Col xl={2} lg={4} md={6}>
                    <DetailField label="Name" value={owner.name} />
                  </Col>
                  <Col xl={2} lg={4} md={6}>
                    <DetailField label="Date of Birth" value={owner.dob} />
                  </Col>
                  <Col xl={2} lg={4} md={6}>
                    <DetailField
                      label="Nationality"
                      value={owner.nationality}
                    />
                  </Col>
                  <Col xl={2} lg={4} md={6}>
                    <DetailField label="ID Type" value={owner.idType} />
                  </Col>
                  <Col xl={2} lg={4} md={6}>
                    <DetailField label="ID Number" value={owner.idNumber} />
                  </Col>
                  <Col xl={2} lg={4} md={6}>
                    <DetailField label="Expiry Date" value={owner.expiryDate} />
                  </Col>
                  <Col xl={4} lg={8} md={6}>
                    <DetailField
                      label="Residential Address"
                      value={owner.address}
                    />
                  </Col>
                  <Col xl={2} lg={4} md={6}>
                    <DetailField
                      label="Ownership Interest (%)"
                      value={owner.interest}
                    />
                  </Col>
                </Row>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-muted fs-12">
              No beneficial owners found
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="d-flex align-items-center mb-4 pt-1">
        <Badge
          bg="none"
          className="me-2 px-3 py-2 fw-bold"
          style={{
            backgroundColor:
              customer.status === "ACTIVATED" ? "#E1F9ED" : "#FFF3E0",
            color: customer.status === "ACTIVATED" ? "#28C76F" : "#FF9F43",
            borderRadius: "6px",
            fontSize: "10px",
            textTransform: "uppercase",
          }}
        >
          {customer.status || "PENDING"}
        </Badge>
        <Badge
          bg="none"
          className="px-3 py-2 fw-bold"
          style={{
            backgroundColor:
              customer.kycStatus === "APPROVED" ? "#E1F9ED" : "#FFF3E0",
            color: customer.kycStatus === "APPROVED" ? "#28C76F" : "#FF9F43",
            borderRadius: "6px",
            fontSize: "10px",
            textTransform: "uppercase",
          }}
        >
          {customer.kycStatus || "PENDING"}
        </Badge>
        <div className="ms-auto">
          <button
            className="btn btn-outline-light btn-sm text-dark d-flex align-items-center shadow-none px-3"
            style={{
              border: "1px solid #d5dfe7",
              fontSize: "12px",
              height: "36px",
              borderRadius: "8px",
            }}
            onClick={onEdit}
          >
            <i className="far fa-edit me-2"></i> Edit
          </button>
        </div>
      </div>

      {isOrganization ? renderOrganizationInfo() : renderIndividualInfo()}

      <div className="mb-4">
        <h6 className="fw-bold mb-3 fs-13 text-black text-uppercase letter-spacing-1">
          Customer Remarks
        </h6>
        <div
          className="border p-5 mb-4 bg-light text-center border-light d-flex flex-column align-items-center justify-content-center"
          style={{ borderRadius: 12, minHeight: "160px" }}
        >
          <i className="far fa-comment-dots fs-30 text-muted mb-3"></i>
          <span className="text-muted fs-13">
            No remarks available for this customer
          </span>
        </div>
      </div>
    </>
  );
};

export default InfoTab;
